import { useState } from "react";

import { ClipboardCheck, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  useCreateRecipe,
  useDeleteRecipe,
  useRecipeChangeRequests,
  useRecipes,
  useSetDefaultRecipe,
  useUpdateRecipe,
} from "~/hooks/production";
import { useProducts } from "~/hooks/useProducts";
import { useProfileStore } from "~/store/profileStore";
import type { RecipeFormData, SkuRecipe } from "~/types/production";

import { RecipeDialog, type RecipeSubmitMeta } from "./components/RecipeDialog";
import { RecipesTable } from "./components/RecipesTable";

export function RecipesPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<SkuRecipe | undefined>(
    undefined
  );
  const [deleteRecipe, setDeleteRecipe] = useState<SkuRecipe | undefined>(
    undefined
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [productFilter, setProductFilter] = useState<string>("all");

  const filters = {
    product_id: productFilter !== "all" ? productFilter : undefined,
  };

  const { data: recipes = [], isLoading } = useRecipes(filters);

  // Filter client-side so a search matches the product CODE (sku) and product
  // name, not just the recipe name. The dataset is small (a few dozen recipes).
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredRecipes = normalizedSearch
    ? recipes.filter((recipe) => {
        const haystack = [
          recipe.name,
          recipe.product?.short_name,
          recipe.product?.sku,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(normalizedSearch);
      })
    : recipes;
  const { data: products = [] } = useProducts();
  const createMutation = useCreateRecipe();
  const updateMutation = useUpdateRecipe();
  const deleteMutation = useDeleteRecipe();
  const setDefaultMutation = useSetDefaultRecipe();

  const navigate = useNavigate();
  const { role } = useProfileStore();
  const {
    request: requestChange,
    isRequesting,
    pendingCount,
  } = useRecipeChangeRequests();

  // superadmin/admin can write recipes directly (RLS). Everyone else who reaches
  // this page (produccion-admin, logistica) must route edits through an
  // authorization request. produccion-admin + superadmin/admin can approve.
  const canEditDirect = role === "superadmin" || role === "admin";
  const isApprover =
    role === "superadmin" || role === "admin" || role === "produccion-admin";
  const dialogMode: "direct" | "request" = canEditDirect ? "direct" : "request";

  const handleCreate = () => {
    setEditingRecipe(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (recipe: SkuRecipe) => {
    setEditingRecipe(recipe);
    setDialogOpen(true);
  };

  const handleSubmit = (data: RecipeFormData, meta?: RecipeSubmitMeta) => {
    // Proposer path: an edit becomes a pending authorization request.
    if (editingRecipe && dialogMode === "request") {
      requestChange({
        recipeId: editingRecipe.id,
        payload: data,
        changes: meta?.changes ?? [],
        reason: meta?.reason ?? "",
      })
        .then(() => {
          setDialogOpen(false);
          setEditingRecipe(undefined);
        })
        .catch(() => {
          /* toast handled in hook */
        });
      return;
    }

    if (editingRecipe) {
      updateMutation.mutate(
        { id: editingRecipe.id, data },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingRecipe(undefined);
          },
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          setDialogOpen(false);
        },
      });
    }
  };

  const handleDelete = (recipe: SkuRecipe) => {
    setDeleteRecipe(recipe);
  };

  const confirmDelete = () => {
    if (deleteRecipe) {
      deleteMutation.mutate(deleteRecipe.id, {
        onSuccess: () => {
          setDeleteRecipe(undefined);
        },
      });
    }
  };

  const handleSetDefault = (recipe: SkuRecipe) => {
    setDefaultMutation.mutate(recipe.id);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Recetas"
        description={
          canEditDirect
            ? "Gestión de recetas (bill of materials) para productos"
            : "Edita una receta para enviar el cambio a autorización del área"
        }
        actions={
          <div className="flex flex-col gap-2 sm:flex-row">
            {isApprover && (
              <Button
                variant="outline"
                className="h-9"
                onClick={() => navigate("/production/recipes/aprobaciones")}
              >
                <ClipboardCheck className="mr-2 h-4 w-4" />
                Aprobaciones
                {pendingCount > 0 && (
                  <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white">
                    {pendingCount}
                  </span>
                )}
              </Button>
            )}
            {canEditDirect && (
              <Button onClick={handleCreate} className="h-9">
                <Plus className="mr-2 h-4 w-4" />
                Nueva Receta
              </Button>
            )}
          </div>
        }
      />

      {/* Filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Select value={productFilter} onValueChange={setProductFilter}>
          <SelectTrigger className="h-9 w-full sm:w-auto sm:min-w-[180px]">
            <SelectValue placeholder="Todos los productos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los productos</SelectItem>
            {products.map((product) => (
              <SelectItem key={product.id} value={product.id}>
                {product.sku
                  ? `${product.sku} - ${product.short_name}`
                  : product.short_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative w-full sm:w-auto sm:min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por código, producto o receta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 w-full pl-8"
          />
        </div>

        <span className="text-sm text-muted-foreground">
          {filteredRecipes.length} recetas
        </span>
      </div>

      <Card>
        <CardContent className="p-0">
          <RecipesTable
            recipes={filteredRecipes}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSetDefault={handleSetDefault}
          />
        </CardContent>
      </Card>

      <RecipeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        recipe={editingRecipe}
        onSubmit={handleSubmit}
        isLoading={
          createMutation.isPending ||
          updateMutation.isPending ||
          isRequesting
        }
        mode={editingRecipe ? dialogMode : "direct"}
      />

      <AlertDialog
        open={!!deleteRecipe}
        onOpenChange={(open) => !open && setDeleteRecipe(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Receta</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar la receta "{deleteRecipe?.name}
              "? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
